<template>
  <div
    v-if="buyFlowStore.pageState === buyFlowPageStates.selectPlan || buyFlowStore.pageState === buyFlowPageStates.email"
  ></div>
  <div v-else-if="isLoading">
    <LoadingSpinner />
  </div>
  <section v-else id="payment-section">
    <!--header-->
    <h1 class="opposite-ends">
      <span class="h1">{{ tn("payment") }}</span>
      <button
        v-if="buyFlowStore.pageState === buyFlowPageStates.reviewOrder"
        id="btnEditPayment"
        type="button"
        class="text-sm inline-btn-link no-print"
        @click="editPayment()"
      >
        {{ t("Common.edit") }}
      </button>
    </h1>
    <div v-if="displayEnterEmailMessage">{{ tn("enterEmail") }}</div>
    <!-- This might be useful when we have the select card page UX -- So not removing it for now -->
    <!-- <h2 v-if="userStore.currentUser" class="mb">{{ tn("orderFor", { userName: userFullName }) }}</h2> -->
    <ul class="tight">
      <li v-if="FLAGS.ENABLE_BUYFLOW_PAYPAL === 'true'" class="choose-cc-or-pp constrain-25">
        <label class="flex-start pa-half" :class="paymentType == 'creditCard' ? 'selected' : ''">
          <input
            id="payment-type-cc"
            v-model="paymentType"
            type="radio"
            name="paymentType"
            value="creditCard"
            class="mr-quarter"
            @click="setPaymentType('creditCard')"
          />
          <svg-icon-component icon="credit-card" class="mr-quarter" />
          {{ tn("paymentTypeCC") }}
        </label>
      </li>
      <li v-if="FLAGS.ENABLE_BUYFLOW_PAYPAL === 'true'" class="choose-cc-or-pp constrain-25">
        <label class="flex-start pa-half" :class="paymentType == 'paypal' ? 'selected' : ''">
          <input
            id="payment-type-pp"
            v-model="paymentType"
            type="radio"
            name="paymentType"
            value="paypal"
            class="mr-quarter"
            @click="setPaymentType('paypal')"
          />
          <svg-icon-component icon="paypal-icon" class="mr-quarter" />
          {{ tn("paymentTypePP") }}
        </label>
      </li>
      <li v-if="FLAGS.ENABLE_BUYFLOW_PAYPAL === 'true'" v-show="paymentType == 'paypal'" class="mt">
        <button class="btn-shell" @click="initiatePayPalRequest()">
          <svg-icon-component icon="paypal-button" class="paypal-svg" />
        </button>
      </li>
      <li v-show="paymentType == 'creditCard'" class="mt">
        <section v-if="displayCreditCardDropDown" id="credit-card-drop-down" class="constrain-65">
          <SelectPayment
            :credit-cards-prop="buyFlowStore.creditCards"
            :show-details="paymentPageStore.pageState === pageStates.displayCards && userStore.isEstablishedAccount"
            @add-card="addCard"
            @selected-credit-card="prepareNextStep"
            @set-display-card-details-state="displaySelectedCardDetails"
          />
        </section>
        <section v-show="displayAddCreditCard" id="credit-card-add-section">
          <BuyAddCardComponent
            @payment-method-added="prepareNextStep"
            @payment-method-error="displayAddCreditCardError"
          />
        </section>
        <section v-if="displaySelectedCreditCard" class="constrain-65">
          <BuySelectedCreditCardComponent :selected-payment-method-id="buyFlowStore.cart?.paymentMethodId || ''" />
        </section>
      </li>
      <li v-if="buyFlowStore.pageState === buyFlowPageStates.payment" class="mt">
        <svg-icon-component v-if="!buyFlowStore.hasWebrootCartItems()" icon="trust-badge-30" class="no-shrink mr" />
        <svg-icon-component v-if="buyFlowStore.hasWebrootCartItems()" icon="trust-badge-70" class="no-shrink mr" />
        <svg-icon-component icon="trust-badge-support" class="no-shrink mr" />
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { t } from "@/i18n";
import { ref, onBeforeMount, watch, computed } from "vue";
import { FLAGS } from "@/define";
import { logEvent, logException } from "@/common/logger";
import BuyAddCardComponent from "./AddCreditCard.vue";
//import BuySelectPaymentComponent from "./SelectPayment.vue";
import BuySelectedCreditCardComponent from "./SelectedCreditCard.vue";
import LoadingSpinner from "@/components/shared/LoadingSpinner.vue";
import { useUserStore } from "@/stores/user";
import { useNotificationsStore, NotificationType } from "@/stores/notifications";
import { pageStates } from "@/components/Buy/Payment/PaymentConfigs";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { usePaymentPageStore } from "@/stores/paymentPage";
import { loadCreditCards } from "@/components/Buy/BuyHelper";
import { changePageState } from "@/components/Buy/BuyHelper";
import { layoutStates, pageStates as buyFlowPageStates } from "@/components/Buy/BuyEnums";
import SelectPayment from "@/components/Buy/Payment/SelectPayment.vue";
import { cartUpdate } from "@/common/googleTagEvents";
import SvgIconComponent from "@/components/shared/SvgIcon/SvgIcon.vue";

import { AxiosError } from "axios";
import { handleApiError } from "@/common/handleApiError";
import { unifiedApi } from "@/common";
import { IPayPalAgreementTokenResponse } from "@/common/api/unifiedPortal/interfaces";

const componentName = "BuyPaymentComponent";

logEvent("created", componentName);
const isLoading = ref(false);
const paymentType = ref("creditCard");
const paymentPageStore = usePaymentPageStore();
const userStore = useUserStore();
const notificationsStore = useNotificationsStore();
const buyFlowStore = useBuyFlowStore();

const displayEnterEmailMessage = computed(() => {
  return buyFlowStore.pageState === buyFlowPageStates.email;
});

const displayAddCreditCard = computed(() => {
  return (
    buyFlowStore.pageState === buyFlowPageStates.payment &&
    (paymentPageStore.pageState === pageStates.addCard || paymentPageStore.pageState === pageStates.forceAddCard)
  );
});

const displaySelectedCreditCard = computed(() => {
  return buyFlowStore.pageState === buyFlowPageStates.reviewOrder;
});

const displayCreditCardDropDown = computed(() => {
  return (
    paymentPageStore.pageState !== pageStates.forceAddCard &&
    ((buyFlowStore.pageState === buyFlowPageStates.payment &&
      paymentPageStore.pageState === pageStates.displayCards &&
      !userStore.isEstablishedAccount) ||
      (buyFlowStore.pageState === buyFlowPageStates.payment &&
        userStore.isEstablishedAccount &&
        buyFlowStore.creditCards.length > 0))
  );
});

onBeforeMount(async () => {
  try {
    logEvent("mounting", componentName);
    await loadPage();
  } catch (ex) {
    logException(ex as Error);
  }
});

async function loadPage() {
  isLoading.value = true;
  if (buyFlowStore.pageState === buyFlowPageStates.payment) {
    logEvent("loadPage", componentName, paymentPageStore.pageState);
    //If there was an error we want to force the CC form open
    if (paymentPageStore.pageState === pageStates.forceAddCard) {
      const card = buyFlowStore.creditCards.find(l => l.id === buyFlowStore.cart?.paymentMethodId);
      logEvent("failed card data", componentName, card);
      if (card) {
        paymentPageStore.creditAddress = {
          creditCardAddress1: card.address.streetAddress1 || "",
          creditCardAddress2: card.address.streetAddress2 || "",
          creditCardPostalCode: card.address.postalCode || "",
          creditCardCity: card.address.city || "",
          creditCardCountry: card.address.country || "",
          creditCardState: card.address.stateOrProvince || "",
        };
      }
      isLoading.value = false;
      return;
    }
    paymentPageStore.pageState = pageStates.addCard;
    logEvent("changing payment page state", componentName, paymentPageStore.pageState);

    if (userStore.isEstablishedAccount) {
      logEvent("established Account", componentName, userStore.currentUser);
      buyFlowStore.creditCards = (await loadCreditCards()) || [];

      if (buyFlowStore.cart && !buyFlowStore.cart?.paymentMethodId) {
        const card = buyFlowStore.creditCards.find(l => l.defaultPaymentMethod);
        if (card) {
          logEvent("setting card to cart", componentName, card.id);
          buyFlowStore.cart.paymentMethodId = card.id;
        }
      }
      // we need to sync this according to the buyflow page state
      buyFlowStore.checkSalesTax = true;
      await buyFlowStore.updateShoppingCartTotal();
      buyFlowStore.checkSalesTax = false;

      if (buyFlowStore.creditCards.length > 0 && userStore.isEstablishedAccount) {
        paymentPageStore.pageState = pageStates.displayCards;
        logEvent("changing payment page state", componentName, paymentPageStore.pageState);
      }
    } else {
      buyFlowStore.updateShoppingCartTotal();
    }
  }
  isLoading.value = false;
}

function tn(v: string, params?: Record<string, unknown>): string {
  return t(`${componentName}.${v}`, params);
}

function setPaymentType(type: string) {
  paymentType.value = type;
}

function displaySelectedCardDetails() {
  paymentPageStore.pageState = pageStates.displayCards;
}

async function initiatePayPalRequest() {
  logEvent("Initiating paypal transaction", componentName);
  try {
    const paypalAgreementTokenResponse: IPayPalAgreementTokenResponse = (await unifiedApi.createPayPalAgreementToken())
      .data;
    logEvent("Got the paypal agreement token, redirecting to paypal approval url", componentName);
    sessionStorage.setItem("PayPal_token_id", paypalAgreementTokenResponse.tokenId);
    window.location.href = paypalAgreementTokenResponse.approvalUrl;
  } catch (error) {
    handleApiError(error as AxiosError, true);
  }
}

function addCard() {
  paymentPageStore.pageState = pageStates.addCard;
}

function displayAddCreditCardError(error: string) {
  logEvent("displayAddCreditCardError", componentName, error);
  notificationsStore.clearNotifications();
  notificationsStore.addNotification({ type: error as NotificationType });
  if (paymentPageStore.pageState === pageStates.forceAddCard) {
    paymentPageStore.pageState = pageStates.addCard;
  } else {
    paymentPageStore.pageState =
      buyFlowStore.creditCards && buyFlowStore.creditCards.length > 0 ? pageStates.displayCards : pageStates.addCard;
  }
  logEvent("displayAddCreditCardError change state", componentName, paymentPageStore.pageState);
}

async function prepareNextStep(creditCardId: string, hasAddedCreditCard: boolean) {
  isLoading.value = true;
  logEvent("prepareNextStep", componentName, { creditCardId: creditCardId, hasAddedCreditCard: hasAddedCreditCard });

  if (hasAddedCreditCard) {
    buyFlowStore.creditCards = (await loadCreditCards()) || [];
    const selectedCreditCard = buyFlowStore.creditCards.find(c => c.id === creditCardId);

    if (selectedCreditCard) {
      buyFlowStore.updateCreditCard(selectedCreditCard?.id);
    }

    buyFlowStore.checkSalesTax = true;
    buyFlowStore.updateShoppingCartTotal(true);
    buyFlowStore.checkSalesTax = false;
  }

  buyFlowStore.showProcessingOrderMessage = false;
  paymentPageStore.pageState = pageStates.selectedCard;
  cartUpdate(buyFlowStore.cart, buyFlowPageStates.reviewOrder);
  changePageState(buyFlowPageStates.reviewOrder);
  isLoading.value = false;
}

watch(
  () => buyFlowStore.triggerStateChange,
  async () => {
    await loadPage();
  }
);

// If cart is being updated, we want to hide the payment information
// from the user until we get the response back because preview may contain payment errors
watch(
  () => buyFlowStore.isCalculatingCart,
  async () => {
    isLoading.value = buyFlowStore.isCalculatingCart;
  }
);

function editPayment() {
  buyFlowStore.layoutState = layoutStates.editPayment;
}
</script>
